import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  TourDefinition,
  TourGroup,
  estimatedDurationLabel,
} from '../../domain/tour-definition';
import { LEARN_ENTRIES, LearnListEntry, LearnAction } from '../../domain/tour-registry';
import { TourController } from '../../engine/tour-controller.service';
import { TourProgressService, TourStatus } from '../../engine/tour-progress.service';
import { LearningCenterService } from '../../engine/learning-center.service';
import { KeepItPanelsService } from '../../../keep-it/presentation/keep-it-panels.service';

interface ViewEntry {
  kind: 'tour' | 'group' | 'action';
  tour?: TourDefinition;
  group?: TourGroup;
  action?: LearnAction;
}

/**
 * The "Learn VectorMarine" home screen — a revisitable list of feature tours,
 * not a first-run flow. A full-screen overlay opened from the profile help
 * button. Faithful port of Flutter's `LearnVectorMarineScreen`.
 */
@Component({
  selector: 'app-learning-center',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './learning-center.component.html',
  styleUrl: './learning-center.component.scss',
})
export class LearningCenterComponent {
  private controller = inject(TourController);
  private progress = inject(TourProgressService);
  private service = inject(LearningCenterService);
  private keepItPanels = inject(KeepItPanelsService);

  readonly isOpen = this.service.isOpen;

  searchMode = signal(false);
  query = signal('');

  /** Tracks which group accordions are expanded (id → open). */
  private expanded = signal<Record<string, boolean>>({});

  durationLabel = estimatedDurationLabel;

  private matches(text: string, q: string): boolean {
    return text.toLowerCase().includes(q);
  }

  readonly isSearching = computed(() => this.query().trim().length > 0);

  /** The rows to show, filtered by the current query (mirrors Flutter logic). */
  readonly visibleEntries = computed<ViewEntry[]>(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) return LEARN_ENTRIES.map(toView);

    const result: ViewEntry[] = [];
    for (const entry of LEARN_ENTRIES) {
      if (entry.kind === 'tour') {
        const t = entry.tour;
        if (this.matches(t.name, q) || this.matches(t.description, q)) {
          result.push({ kind: 'tour', tour: t });
        }
      } else if (entry.kind === 'group') {
        const g = entry.group;
        if (this.matches(g.name, q) || this.matches(g.description, q)) {
          result.push({ kind: 'group', group: g });
        } else {
          for (const t of g.tours) {
            if (this.matches(t.name, q) || this.matches(t.description, q)) {
              result.push({ kind: 'tour', tour: t });
            }
          }
        }
      } else if (entry.kind === 'action') {
        const a = entry.action;
        if (this.matches(a.title, q) || this.matches(a.description, q)) {
          result.push({ kind: 'action', action: a });
        }
      }
    }
    return result;
  });

  status(tourId: string): TourStatus {
    return this.progress.getStatus(tourId);
  }

  completedInGroup(group: TourGroup): number {
    return group.tours.filter((t) => this.progress.getStatus(t.id) === 'completed')
      .length;
  }

  groupSubtitle(group: TourGroup): string {
    return group.description;
  }

  isExpanded(groupId: string): boolean {
    // While searching, matched groups auto-expand.
    if (this.isSearching()) return true;
    return !!this.expanded()[groupId];
  }

  toggleGroup(groupId: string): void {
    if (this.isSearching()) return;
    this.expanded.update((m) => ({ ...m, [groupId]: !m[groupId] }));
  }

  onSearchToggle(on: boolean): void {
    this.searchMode.set(on);
    if (!on) this.query.set('');
  }

  onQuery(value: string): void {
    this.query.set(value);
  }

  startTour(tour: TourDefinition): void {
    void this.controller.startTour(tour.id);
  }

  runAction(action: LearnAction): void {
    if (action.actionId === 'can_i_keep_it') {
      this.close();
      this.keepItPanels.open();
    }
  }

  close(): void {
    this.searchMode.set(false);
    this.query.set('');
    this.service.close();
  }
}

function toView(entry: LearnListEntry): ViewEntry {
  if (entry.kind === 'tour') {
    return { kind: 'tour', tour: entry.tour };
  } else if (entry.kind === 'group') {
    return { kind: 'group', group: entry.group };
  } else {
    return { kind: 'action', action: entry.action };
  }
}
